import { isNativeOAuthState } from "./postAuthRedirect";
import { Capacitor } from "@capacitor/core";
import { App } from "@capacitor/app";
import { Browser } from "@capacitor/browser";
import { Filesystem, Directory } from "@capacitor/filesystem";
import { Share } from "@capacitor/share";

export const isNativeApp = () => Capacitor.isNativePlatform();
export const NATIVE_RESUME_EVENT = "qit:native-resume";

// Return URLs are signals to reconcile against the server, never proof of payment.
export const isNativeReturnUrl = (value: string): boolean => {
    try {
        const url = new URL(value);
        return url.protocol === "qitplaylist:" && url.hostname === "payment-return" &&
            (url.pathname === "" || url.pathname === "/");
    } catch { return false; }
};

export const openExternalBrowser = async (value: string) => {
    const url = new URL(value, window.location.origin);
    if (url.protocol !== "https:") throw new Error("An HTTPS address is required.");
    if (isNativeApp()) await Browser.open({ url: url.href });
    else window.location.assign(url.href);
};

export const closeExternalBrowser = async () => {
    if (isNativeApp()) await Browser.close().catch(() => undefined);
};

let installed = false;
export const installNativeLifecycle = async () => {
    if (!isNativeApp() || installed) return;
    installed = true;
    const resume = () => {
        window.dispatchEvent(new Event("focus"));
        window.dispatchEvent(new Event(NATIVE_RESUME_EVENT));
    };
    await App.addListener("appStateChange", ({ isActive }) => { if (isActive) resume(); });
    await Browser.addListener("browserFinished", resume);
    const onReturn = async (url: string) => {
        const callback = getNativeOAuthCallback(url);
        if (callback) {
            await closeExternalBrowser();
            window.location.replace(callback);
            return;
        }
        if (!isNativeReturnUrl(url)) return;
        const returned = new URL(url);
        const paymentId = returned.searchParams.get("payment_id");
        if (paymentId) {
            if (paymentId !== localStorage.getItem("last_payment_id")) return;
            const params = new URLSearchParams({ payment_id: paymentId });
            for (const key of ["provider", "token"]) {
                const value = returned.searchParams.get(key);
                if (value) params.set(key, value);
            }
            await closeExternalBrowser();
            window.location.assign(`/payment-success?${params}`);
            return;
        }
        await closeExternalBrowser();
        resume();
    };
    await App.addListener("appUrlOpen", ({ url }) => { void onReturn(url); });
    const launch = await App.getLaunchUrl();
    if (launch?.url) await onReturn(launch.url);
};

export const shareNativeUrl = (url: string) => Share.share({ title: "Qit Playlist invitation", url });

export const shareNativeFile = async (blob: Blob, filename: string) => {
    const safeFilename = filename.replace(/[^a-zA-Z0-9._-]/g, "_") || "download";
    const data = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result).split(",")[1]);
        reader.onerror = () => reject(reader.error || new Error("Could not read file"));
        reader.readAsDataURL(blob);
    });
    // Each export owns its cache file, including simultaneous shares.
    const exportId = crypto.randomUUID?.() || Array.from(crypto.getRandomValues(new Uint8Array(16)), byte => byte.toString(16).padStart(2, "0")).join("");
    const path = `qit-exports/${exportId}/${safeFilename}`;
    const file = await Filesystem.writeFile({ path, data, directory: Directory.Cache, recursive: true });
    try {
        await Share.share({ title: filename, files: [file.uri], dialogTitle: "Save or share file" });
    } finally {
        await Filesystem.deleteFile({ path, directory: Directory.Cache }).catch(() => undefined);
    }
};

export const getNativeOAuthCallback = (value: string): string | null => {
    try {
        const url = new URL(value);
        if (url.protocol !== "qitplaylist:" || url.hostname !== "oauth-return" ||
            !["", "/"].includes(url.pathname)) return null;
        const state = url.searchParams.get("state");
        if (!isNativeOAuthState(state) || state !== sessionStorage.getItem("oauth_state")) return null;
        const params = new URLSearchParams({ state: state! });
        const code = url.searchParams.get("code");
        const error = url.searchParams.get("error");
        if (code) params.set("code", code);
        else if (error) params.set("error", error);
        else return null;
        return `/callback?${params}`;
    } catch { return null; }
};

export const prepareNativeOAuth = async (): Promise<{ code_challenge: string; code_challenge_method: string }> => {
    const bytes = crypto.getRandomValues(new Uint8Array(32));
    const verifier = Array.from(bytes, byte => byte.toString(16).padStart(2, "0")).join("");
    const digest = new Uint8Array(await crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier)));
    const challenge = btoa(String.fromCharCode(...digest)).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
    sessionStorage.setItem("qit:native-oauth-verifier", verifier);
    return { code_challenge: challenge, code_challenge_method: "S256" };
};
